import React, { useState } from "react";
import { Link, graphql, navigate, useStaticQuery } from "gatsby";

import LocationMark from "../components/icons/Locations/LocationMark";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import BrochureRequestPanel from "../components/BrochureRequestPanel";
import TrustpilotInline from "../components/TrustpilotInline";
import { MicroCombo } from "../components/Trustpilot/TrustpilotWidgets";
import { PostcodeCTAInput } from "../components/PostcodeCTA/PostcodeCTA";
import CareTeamMap from "../components/CareTeamMap";
import CallIcon from "../components/icons/CallIcon";
import {
	SINGLE_TIER_ONE,
	SINGLE_TIER_THREE,
	SINGLE_TIER_TWO,
	COUPLE_TIER_ONE,
	COUPLE_TIER_THREE,
	COUPLE_TIER_TWO,
} from "../constants/pricing";
import logoPrimary from "../images/logos/logo_primary.svg";
import "../../scss/main.scss";
import "../../scss/_ppcLanding.scss";

const imagePath = "/assets/marketing-site/images";

const benefits = [
	[
		"01",
		"Carers trained in-house",
		"Care skills, safe practice and the Edyn standard are built in before a carer joins a home.",
	],
	[
		"02",
		"Stay in the home they love",
		"Familiar routines, people, pets and surroundings remain part of everyday life.",
	],
	[
		"03",
		"Care tailored to the person",
		"A considered plan shapes support around needs today and can adapt as circumstances change.",
	],
	[
		"04",
		"CQC regulated, fully managed care",
		"A wider Edyn team stays accountable for quality, continuity and family communication.",
	],
	[
		"05",
		"Care can start quickly",
		"A responsive team helps families move at the right pace without compromising on the match.",
	],
];

const comparisonRows = [
	["We train our carers in-house", false, false, true],
	["One-to-one carer support", false, true, true],
	["Dedicated care team", false, true, true],
	["Comfort of your own home", false, true, true],
	["Connection to your community", false, true, true],
	["Oversight of care, nutrition and medication", true, false, true],
	["Understanding of wellbeing", false, false, true],
	["In-field quality assurance team", true, false, true],
	["Family can visit and stay at any time", false, true, true],
];

const processSteps = [
	[
		"01",
		"Talk to a Family Care Advisor",
		"Tell us what is changing, what matters at home and when support may be needed.",
		[
			{
				src: `${imagePath}/team/matthew.png`,
				alt: "Matthew, an Edyn Family Care Advisor",
			},
		],
		{
			label: "Arrange a call",
			href: "/care-consultation/",
		},
	],
	[
		"02",
		"Complete a care assessment",
		"A Care Manager gets to know the person, their routines, needs and home environment.",
		[
			{
				src: `${imagePath}/team/orianne.png`,
				alt: "Orianne, an Edyn Care Manager",
			},
		],
	],
	[
		"03",
		"Meet carefully selected carers",
		"Review suitable professional carers and speak with the people you would like to meet.",
		[
			{
				src: `${imagePath}/olivia.png`,
				alt: "Olivia, an Edyn professional carer",
			},
		],
	],
	[
		"04",
		"Begin care with ongoing support",
		"Your care team stays involved, reviews the plan and helps when circumstances change.",
		[
			{
				src: `${imagePath}/olivia.png`,
				alt: "An Edyn professional carer",
			},
			{
				src: `${imagePath}/team/orianne.png`,
				alt: "An Edyn Care Manager",
			},
			{
				src: `${imagePath}/team/sarah.png`,
				alt: "An Edyn Care Coordinator",
			},
		],
	],
];

const conditions = [
	["01", "Dementia care", "/condition/dementia/"],
	["02", "Parkinson's care", "/condition/parkinsons/"],
	["03", "Multiple sclerosis care", "/condition/multiple-sclerosis/"],
	["04", "Cancer care", "/condition/cancer/"],
	["05", "Stroke care", "/condition/stroke/"],
	["06", "Care for couples", "/couples-care/"],
];

const careTiers = [
	{
		number: "01",
		label: "Tier one",
		title: "Standard live-in care",
		description:
			"Everyday support that keeps home life running smoothly, with practical help, personal care and companionship at the person's preferred pace.",
		singlePrice: SINGLE_TIER_ONE,
		couplePrice: COUPLE_TIER_ONE,
		includes: [
			"Household tasks and support around the home",
			"Medication support",
			"Personal hygiene, dressing and toileting assistance",
			"Companionship and emotional support",
			"Some night-time care, typically twice a night or less",
		],
		tone: "one",
	},
	{
		number: "02",
		label: "Tier two",
		title: "Specialist live-in care",
		description:
			"For people with more involved needs, including conditions such as dementia or Parkinson's and support with routines that need a little more coordination.",
		singlePrice: SINGLE_TIER_TWO,
		couplePrice: COUPLE_TIER_TWO,
		includes: [
			"Household tasks and medication support",
			"More extensive personal care, including stoma, PEG or catheter support",
			"Hoisting where one person can complete it safely",
			"Help with challenging behaviour, such as refusing medication",
			"Carer breaks planned around the care schedule",
		],
		tone: "two",
	},
	{
		number: "03",
		label: "Tier three",
		title: "Advanced live-in care",
		description:
			"For significant care needs throughout the day, where continuous and specialist assistance makes life at home safer and more manageable.",
		singlePrice: SINGLE_TIER_THREE,
		couplePrice: COUPLE_TIER_THREE,
		includes: [
			"Support with all household tasks",
			"Total support for personal needs, including stoma, PEG or catheter care",
			"Complex one-to-one care and condition-specific support",
			"Hoisting that requires more than one carer",
			"Careful planning so there is no gap in support during carer breaks",
		],
		tone: "three",
	},
];

const familyReviews = [
	{
		title: "Very impressed with Edyn Care",
		copy:
			"The team took care to ensure the carer matched the requirements we discussed and regularly follow up to ensure all needs are being met.",
		author: "Verified family review",
	},
	{
		title: "Excellent care from start to finish",
		copy:
			"Kindness and care from the moment we made contact. The team listened and made the journey through carer selection, contracts and care feel easy to navigate.",
		author: "Susie Blakeney, Edyn family",
	},
];

const carerBenefits = [
	"A dedicated carer who gets to know your loved one",
	"Carefully matched to your family by our care team",
	"24/7 access to our care management team",
	"Regular updates, so you always know how your loved one is doing",
	"Nutritious home-cooked meals, planned around their preferences",
	"Support with daily routines, from mobility to medication",
];

const proofStats = [
	["500+", "families helped"],
	["4.8 \u2605", "on Trustpilot", "4.8 stars on Trustpilot"],
	["4.5 \u2605", "on Google", "4.5 stars on Google"],
	["11m", "care hours delivered"],
	["2.7m", "cups of tea shared"],
];

const salesTeamPortraits = [
	{
		src: `${imagePath}/team/daniel-new-160.webp`,
		alt: "Daniel, an Edyn Family Care Advisor",
	},
	{
		src: `${imagePath}/team/matthew-160.webp`,
		alt: "Matthew, an Edyn Family Care Advisor",
	},
	{
		src: `${imagePath}/team/desre-160.webp`,
		alt: "Desre, an Edyn Family Care Advisor",
	},
];

const ProofRibbonGroup = ({ duplicate = false }) => (
	<div
		className="home-proof-ribbon__group"
		aria-hidden={duplicate ? "true" : undefined}
	>
		{proofStats.map(([value, label, ariaLabel]) => (
			<p className="home-proof-ribbon__stat" key={`${value}-${label}`}>
				<strong aria-label={ariaLabel}>{value}</strong>
				<span>{label}</span>
			</p>
		))}
		<div className="home-proof-ribbon__press">
			<p>As seen in</p>
			<div>
				<img
					src={`${imagePath}/thegaurdian.png`}
					alt={duplicate ? "" : "The Guardian"}
				/>
				<img
					src={`${imagePath}/thetimes.png`}
					alt={duplicate ? "" : "The Times"}
				/>
				<img
					src={`${imagePath}/theeveningstandard.png`}
					alt={duplicate ? "" : "Evening Standard"}
				/>
			</div>
		</div>
	</div>
);

const Mark = ({ yes }) => (
	<span
		className={`ppc-compare__mark ${yes ? "is-yes" : "is-no"}`}
		aria-label={yes ? "Included" : "Not included"}
	>
		{yes ? "\u2713" : "\u00d7"}
	</span>
);

export default function LiveInCareSouthEastLondon() {
	const { site } = useStaticQuery(graphql`
		query LiveInCareSouthEastLondonPageQuery {
			site {
				siteMetadata {
					appConfig {
						gtmContainerId
					}
				}
			}
		}
	`);

	const {
		siteMetadata: {
			appConfig: { gtmContainerId },
		},
	} = site;
	const [postcode, setPostcode] = useState("");
	const [reviewIndex, setReviewIndex] = useState(0);

	const submitPostcode = (event) => {
		event.preventDefault();
		navigate(
			`/liveincare/looking-for-care/?postcode=${encodeURIComponent(
				postcode.trim()
			)}`
		);
	};
	const showReview = (offset) => {
		setReviewIndex(
			(current) =>
				(current + offset + familyReviews.length) % familyReviews.length
		);
	};

	const renderCareEnquiryCta = (sectionClassName = "") => (
		<section className={`pricing-contact ${sectionClassName}`.trim()}>
			<div className="site-shell pricing-contact__enquiry">
				<h2>Two ways to start your care enquiry</h2>
				<div className="pricing-contact__options">
					<article className="pricing-contact__option pricing-contact__option--call">
						<p className="pricing-contact__option-label">
							<CallIcon aria-hidden="true" />
							<span>Speak to a friendly care advisor</span>
						</p>
						<a className="pricing-contact__call" href="tel:02039709902">
							020 3970 9902
						</a>
						<div
							className="pricing-contact__team"
							role="img"
							aria-label="Daniel, Matthew and Desre from the Edyn sales team"
						>
							{salesTeamPortraits.map((person) => (
								<img
									key={person.src}
									src={person.src}
									alt={person.alt}
									loading="lazy"
									width="160"
									height="160"
								/>
							))}
						</div>
					</article>
					<article className="pricing-contact__option pricing-contact__option--area">
						<p className="pricing-contact__option-label">
							<LocationMark aria-hidden="true" />
							<span>Check your area</span>
						</p>
						<h3>Find care options near you</h3>
						<div className="pricing-contact__form">
							<PostcodeCTAInput targetPath="/liveincare/looking-for-care/" />
						</div>
					</article>
				</div>
				<div className="pricing-contact__trustpilot">
					<p>Trusted by families arranging fully managed live-in care.</p>
					<MicroCombo
						classNames="pricing-contact__trustpilot-widget"
						alignment="left"
						width="270px"
					/>
				</div>
			</div>
		</section>
	);

	return (
		<div className="edyn-redesign-shell edyn-location-redesign edyn-london ppc-care-page">
			<Seo
				title="Live-in Care Across London & the South East | Edyn Care"
				description="Personalised, fully managed live-in care across London and the South East, helping your loved one remain safely in the home they know."
				path="/live-in-care-south-east-london/"
				service={{
					name: "Live-in care across London and the South East",
					areaServed: ["London", "South East England"],
				}}
				breadcrumbs={[
					{ name: "Home", path: "/" },
					{
						name: "London and South East live-in care",
						path: "/live-in-care-south-east-london/",
					},
				]}
			/>
			<Header phoneNumber="020 3970 9902" phoneHref="tel:02039709902" />

			<main id="main-content">
				<section className="london-hero">
					<div className="site-shell london-hero__panel">
						<div className="london-hero__copy">
							<p className="eyebrow eyebrow--light">
								Live-in care across London &amp; the South East
							</p>
							<h1>Live-in care across the South East &amp; London</h1>
							<p className="london-hero__lead">
								Personalised, fully managed live-in care that helps someone
								remain safely in the home and community they know.
							</p>
							<div className="ppc-hero__advisor">
								<div
									className="ppc-hero__advisor-stack"
									role="img"
									aria-label="Daniel, Matthew and Desre from the Edyn sales team"
								>
									{salesTeamPortraits.map((person) => (
										<img
											key={person.src}
											src={person.src}
											alt={person.alt}
											width="160"
											height="160"
										/>
									))}
								</div>
								<div className="ppc-hero__advisor-copy">
									<span>Speak with a friendly care advisor</span>
									<a href="tel:02039709902">020 3970 9902</a>
								</div>
							</div>
							<form
								className="ppc-postcode london-hero__postcode"
								onSubmit={submitPostcode}
							>
								<label htmlFor="ppc-postcode-hero">
									Find care options near you
								</label>
								<div>
									<input
										id="ppc-postcode-hero"
										value={postcode}
										onChange={(event) => setPostcode(event.target.value)}
										placeholder="Enter your postcode"
										required
									/>
									<button type="submit">Check your area</button>
								</div>
							</form>
							<TrustpilotInline className="london-hero__trust" />
						</div>

						<div className="london-hero__visual">
							<div className="london-hero__image">
								<picture>
									<source
										type="image/webp"
										srcSet={`${imagePath}/carer-helping-with-haircut-800.webp 800w, ${imagePath}/carer-helping-with-haircut-1600.webp 1600w`}
										sizes="(max-width: 900px) 100vw, 50vw"
									/>
									<img
										src={`${imagePath}/carer-helping-with-haircut-1600.webp`}
										width="1600"
										height="1463"
										alt="A professional carer supporting an older person at home"
										loading="eager"
										decoding="async"
									/>
								</picture>
							</div>
							<div className="london-hero__location">
								<LocationMark className="icon-default" />
								<p>
									<strong>Care across London &amp; the South East</strong>
									Local teams, fully managed by Edyn.
								</p>
							</div>
						</div>
					</div>
				</section>

				<section
					className="home-proof-ribbon"
					aria-label="Edyn Care in numbers and the press"
				>
					<div className="home-proof-ribbon__viewport">
						<div className="home-proof-ribbon__track">
							<ProofRibbonGroup />
							<ProofRibbonGroup duplicate />
						</div>
					</div>
				</section>

				<section className="home-life section-space">
					<div className="site-shell home-life__grid">
						<div className="home-life__story">
							<p className="eyebrow">More than a professional carer</p>
							<h2>Why Families Choose Edyn Care</h2>
							<figure className="home-life__visual">
								<picture>
									<source
										type="image/webp"
										srcSet={`${imagePath}/livein-imageclus-10-640.webp 640w, ${imagePath}/livein-imageclus-10-1200.webp 1200w`}
										sizes="(max-width: 900px) 100vw, 45vw"
									/>
									<img
										src={`${imagePath}/livein-imageclus-10-1200.webp`}
										width="1200"
										height="1810"
										alt="A Care Advisor discussing a care plan with an older man"
										loading="lazy"
										decoding="async"
									/>
								</picture>
								<figcaption>
									<span aria-hidden="true">&#10003;</span>
									Care planned around real life.
								</figcaption>
							</figure>
						</div>
						<div className="home-life__content">
							<p className="section-lead">
								A fully managed service combines a carefully chosen professional
								carer with experienced people who plan, coordinate and oversee
								care.
							</p>
							<ul className="home-life__list">
								{benefits.map(([number, title, copy]) => (
									<li key={number}>
										<span>{number}</span>
										<p>
											<strong>{title}</strong> {copy}
										</p>
									</li>
								))}
							</ul>
						</div>
					</div>
				</section>

				<section className="ppc-comparison section-space section-soft">
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow">Understand the options</p>
								<h2>Live-in care, clearly compared.</h2>
							</div>
							<p className="section-lead">
								See the practical differences between a care home, an
								introductory agency and Edyn&apos;s fully managed service.
							</p>
						</div>
						<div className="ppc-compare__scroll">
							<table className="ppc-compare">
								<thead>
									<tr>
										<th>How we compare</th>
										<th>Care home</th>
										<th>Introductory agency</th>
										<th className="is-edyn">
											<img src={logoPrimary} alt="Edyn Care" />
										</th>
									</tr>
								</thead>
								<tbody>
									{comparisonRows.map(([label, home, agency, edyn]) => (
										<tr key={label}>
											<th>{label}</th>
											<td>
												<Mark yes={home} />
											</td>
											<td>
												<Mark yes={agency} />
											</td>
											<td className="is-edyn">
												<Mark yes={edyn} />
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				</section>

				{renderCareEnquiryCta("pricing-contact--comparison")}

				<section className="ppc-review section-space">
					<div className="site-shell ppc-review__inner">
						<div className="ppc-review__heading">
							<p className="eyebrow eyebrow--light">
								What families say about us
							</p>
							<h2>Kind words from families we support.</h2>
							<TrustpilotInline />
						</div>
						<div className="ppc-review__carousel" aria-live="polite">
							<article
								className="ppc-review__entry"
								key={familyReviews[reviewIndex].title}
							>
								<div
									className="ppc-review__stars"
									aria-label="Five out of five stars"
								>
									<span aria-hidden="true">
										&#9733;&#9733;&#9733;&#9733;&#9733;
									</span>
									<strong>Trustpilot</strong>
								</div>
								<h3>{familyReviews[reviewIndex].title}</h3>
								<p>&ldquo;{familyReviews[reviewIndex].copy}&rdquo;</p>
								<span>{familyReviews[reviewIndex].author}</span>
							</article>
							<div className="ppc-review__controls">
								<button
									type="button"
									onClick={() => showReview(-1)}
									aria-label="Previous testimonial"
								>
									&#8592;
								</button>
								<div
									className="ppc-review__dots"
									aria-label="Choose a testimonial"
								>
									{familyReviews.map((review, index) => (
										<button
											type="button"
											key={review.title}
											className={index === reviewIndex ? "is-active" : ""}
											onClick={() => setReviewIndex(index)}
											aria-label={`Show testimonial ${index + 1}`}
											aria-current={index === reviewIndex ? "true" : undefined}
										/>
									))}
								</div>
								<button
									type="button"
									onClick={() => showReview(1)}
									aria-label="Next testimonial"
								>
									&#8594;
								</button>
							</div>
						</div>
					</div>
				</section>

				<section className="home-process section-space" id="how-care-starts">
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow">A supported start</p>
								<h2>We make arranging care clear.</h2>
							</div>
							<p>
								You can begin with a conversation. We will explain each next
								step and help your family make a considered decision.
							</p>
						</div>
						<ol className="home-process__steps">
							{processSteps.map(([number, title, description, people, cta]) => (
								<li key={number}>
									<div className="home-process__step-head">
										<span>{number}</span>
										<div
											className={`home-process__portraits${
												people.length > 1
													? " home-process__portraits--group"
													: ""
											}`}
										>
											{people.map((person) => (
												<img
													key={person.src}
													src={person.src}
													alt={person.alt}
													loading="lazy"
												/>
											))}
										</div>
									</div>
									<h3>{title}</h3>
									<p>{description}</p>
									{cta && (
										<Link className="home-process__cta" to={cta.href}>
											<span
												className="home-process__cta-status"
												aria-hidden="true"
											/>
											<span>{cta.label}</span>
											<span aria-hidden="true">&#8594;</span>
										</Link>
									)}
								</li>
							))}
						</ol>
						<div className="home-process__reviews">
							<p>
								Families can read independent feedback on the care team
								supporting them, or{" "}
								<a href="tel:02039709902">call 020 3970 9902</a> to talk things
								through with an advisor.
							</p>
							<MicroCombo
								classNames="home-process__trustpilot home-process__trustpilot--desktop"
								alignment="right"
								width="270px"
							/>
							<MicroCombo
								classNames="home-process__trustpilot home-process__trustpilot--mobile"
								alignment="left"
								width="270px"
							/>
						</div>
					</div>
				</section>

				<section
					id="london-specialist"
					className="london-specialist section-space"
				>
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow">Specialist care at home</p>
								<h2>Experience for changing and complex needs.</h2>
							</div>
							<p>
								The right carer is selected for the person, their routines and
								the support their condition requires. Each plan is personal. Our
								care teams can support a range of conditions while protecting
								independence, dignity and the person&apos;s own way of life.
							</p>
						</div>

						<div className="london-specialist__grid">
							{conditions.map(([number, title, url]) => (
								<Link to={url} key={title}>
									<span>{number}</span>
									<strong>{title}</strong>
									<i aria-hidden="true">&#8599;</i>
								</Link>
							))}
						</div>
					</div>
				</section>

				<section className="home-team section-space section-dark">
					<div className="site-shell home-team__grid">
						<div className="home-team__copy">
							<p className="eyebrow eyebrow--light">Fully managed care</p>
							<h2>
								One professional carer in the home. A complete care team behind
								them.
							</h2>
							<p>
								Edyn is not simply an introductory agency. Your professional
								carer is supported by people who understand the care plan,
								coordinate the placement and stay available to the family.
							</p>
							<div className="button-row">
								<Link className="button button--cream" to="/approach/">
									See how Edyn works
								</Link>
								<a
									className="button button--text-light ppc-phone-link"
									href="tel:02039709902"
								>
									Call 020 3970 9902
								</a>
							</div>
						</div>
						<CareTeamMap imagePath={imagePath} />
					</div>
				</section>

				<section className="ppc-carer-team section-space">
					<div className="site-shell ppc-carer-team__grid">
					<div className="ppc-carer-team__image">
						<picture>
							<source
								type="image/webp"
								srcSet={`${imagePath}/livein-imageclus-09-640.webp 640w, ${imagePath}/livein-imageclus-09-1200.webp 1200w`}
								sizes="(max-width: 900px) 100vw, 45vw"
							/>
							<img
								src={`${imagePath}/livein-imageclus-09-1200.webp`}
								width="1200"
								height="1836"
								alt="A professional carer sharing a relaxed moment with an older woman at home"
								loading="lazy"
								decoding="async"
							/>
						</picture>
						</div>
						<div className="ppc-carer-team__copy">
							<p className="eyebrow">Our carers</p>
							<h2>A team who truly cares</h2>
							<p>
								Our carers provide both care and companionship to your loved
								one. We take recruitment seriously: every carer goes through a
								rigorous vetting and interview process before they join our
								team. Our ethos is simple. Carers who are well looked after stay
								longer, which means your loved one gets a familiar, consistent
								face, not a rotating cast of strangers.
							</p>
							<p>
								That consistency is often the biggest relief for families. The
								reassurance that the same trusted person will be there tomorrow,
								and next month.
							</p>
							<p>
								Every carer is carefully selected and matched to your family, so
								you can feel confident from day one.
							</p>
							<ul className="ppc-carer-team__benefits">
								{carerBenefits.map((benefit) => (
									<li key={benefit}>{benefit}</li>
								))}
							</ul>
						</div>
					</div>
				</section>

				<div className="edyn-pricing">
					<section id="pricing-packages" className="pricing-packages">
						<div className="site-shell">
							<div className="pricing-heading">
								<div>
									<p className="eyebrow">Clear pricing</p>
									<h2>No hidden fees. A level of care shaped around need.</h2>
								</div>
								<p>
									The final weekly price is confirmed after assessment, so the
									care plan and cost reflect the support required.
								</p>
							</div>

							<div className="pricing-packages__grid">
								{careTiers.map((tier) => (
									<article
										className={`pricing-package pricing-package--${tier.tone}`}
										key={tier.label}
									>
										<div className="pricing-package__head">
											<div className="pricing-package__meta">
												<span>{tier.number}</span>
												<p>{tier.label}</p>
											</div>
											<h3>{tier.title}</h3>
											<p>{tier.description}</p>
										</div>

										<div className="pricing-package__prices">
											<div>
												<span>One person</span>
												<strong>{tier.singlePrice}</strong>
												<small>per week</small>
											</div>
											<div>
												<span>Two people</span>
												<strong>{tier.couplePrice}</strong>
												<small>per week</small>
											</div>
										</div>

										<div className="pricing-package__includes">
											<p>Included in this package</p>
											<ul>
												{tier.includes.map((item) => (
													<li key={item}>{item}</li>
												))}
											</ul>
										</div>
									</article>
								))}
							</div>

							<div className="pricing-rate-note">
								<span aria-hidden="true">&#10003;</span>
								<p>
									<strong>Every arrangement is personal.</strong> The final
									weekly price reflects the care needed, including any
									night-time, moving-and-handling or additional cover
									requirements.
								</p>
							</div>
						</div>
					</section>
				</div>

				{renderCareEnquiryCta("pricing-contact--pricing")}

				<section className="london-brochure section-space section-clay">
					<div className="site-shell">
						<BrochureRequestPanel
							className="london-brochure__panel"
							gtmContainerId={gtmContainerId}
							headingLevel="h3"
							heading="Send me the Live-in guide"
							description="Tell us a little about your situation and we will email the guide straight to you."
							includeCareFor
							editorialIntro={
								<div className="london-brochure__intro">
									<p className="eyebrow eyebrow--light">
										Your free London &amp; South East care guide
									</p>
									<h2>A clearer way to plan care at home.</h2>
									<p>
										Read it in your own time, share it with family and use it to
										make the next conversation more useful.
									</p>
									<ul>
										<li>How fully managed live-in care works</li>
										<li>What to consider when comparing options</li>
										<li>Questions to ask before care begins</li>
									</ul>
									<div className="london-brochure__note">
										<span aria-hidden="true">PDF</span>
										<p>
											<strong>Delivered by email.</strong>
											No obligation and no pressure to decide today.
										</p>
									</div>
								</div>
							}
						/>
					</div>
				</section>
			</main>

			<Footer phoneNumber="020 3970 9902" phoneHref="tel:02039709902" />
		</div>
	);
}
